// 他参加者が選択しているオブジェクトを、その参加者の色・アイコン・名前で表示するレイヤ。
// PO Board のエピックカード上プレゼンスと同じく、対象の端に小さな参加者バッジを重ねる。

import type { CSSProperties } from "react";
import type { CanvasObject, Viewer } from "@refinement-canvas/domain";
import { useCanvasDocStore } from "./store/canvas-doc-store";
import { useCanvasViewersStore } from "./store/canvas-viewers-store";

interface Props {
  readonly currentUserId: string;
}

interface RemoteSelection {
  readonly key: string;
  readonly object: CanvasObject;
  readonly viewer: Viewer;
  readonly overlapIndex: number;
}

const BADGE_HEIGHT_PX = 28;

export function CanvasRemoteSelectionsLayer({ currentUserId }: Props): JSX.Element {
  const objects = useCanvasDocStore((s) => s.objects);
  const viewers = useCanvasViewersStore((s) => s.viewers);
  const selections = buildRemoteSelections(Object.values(viewers), objects, currentUserId);

  return (
    <div
      data-testid="canvas-remote-selections-layer"
      style={{ position: "absolute", inset: 0, pointerEvents: "none" }}
    >
      {selections.map(({ key, object, viewer, overlapIndex }) => (
        <RemoteSelectionFrame
          key={key}
          object={object}
          overlapIndex={overlapIndex}
          viewer={viewer}
        />
      ))}
    </div>
  );
}

function buildRemoteSelections(
  viewers: readonly Viewer[],
  objects: Record<string, CanvasObject>,
  currentUserId: string
): readonly RemoteSelection[] {
  const overlapCounts = new Map<string, number>();
  const selections: RemoteSelection[] = [];

  for (const viewer of [...viewers].sort(compareViewer)) {
    if (viewer.userId === currentUserId) continue;
    const selectedObjectIds = viewer.selectedObjectIds ?? [];
    for (const objectId of selectedObjectIds) {
      const object = objects[objectId];
      if (!object) continue;
      const overlapIndex = overlapCounts.get(objectId) ?? 0;
      overlapCounts.set(objectId, overlapIndex + 1);
      selections.push({
        key: `${viewer.userId}:${objectId}`,
        object,
        viewer,
        overlapIndex,
      });
    }
  }

  return selections;
}

function RemoteSelectionFrame({
  object,
  overlapIndex,
  viewer,
}: {
  readonly object: CanvasObject;
  readonly overlapIndex: number;
  readonly viewer: Viewer;
}): JSX.Element {
  const color = normalizeViewerColor(viewer.color);
  const labelTop = -(BADGE_HEIGHT_PX + 6) - overlapIndex * (BADGE_HEIGHT_PX + 4);
  const containerStyle: CSSProperties = {
    position: "absolute",
    left: `${object.x}px`,
    top: `${object.y}px`,
    width: `${object.w}px`,
    height: `${object.h}px`,
    pointerEvents: "none",
    zIndex: Number.MAX_SAFE_INTEGER - 1,
  };
  const frameStyle: CSSProperties = {
    position: "absolute",
    inset: 0,
    border: `2px solid ${color}`,
    borderRadius: "8px",
    boxSizing: "border-box",
    transform: object.rotation ? `rotate(${object.rotation}deg)` : undefined,
    transformOrigin: "center",
    boxShadow: `0 0 0 1px rgba(255, 255, 255, 0.9), 0 6px 16px ${withAlpha(color, 0.18)}`,
    pointerEvents: "none",
  };

  return (
    <div
      data-testid={`remote-selection-${viewer.userId}-${object.id}`}
      style={containerStyle}
    >
      <div
        aria-hidden="true"
        data-testid={`remote-selection-frame-${viewer.userId}-${object.id}`}
        style={frameStyle}
      />
      <div
        style={{
          position: "absolute",
          top: `${labelTop}px`,
          left: "0",
          display: "inline-flex",
          alignItems: "center",
          gap: "6px",
          maxWidth: "240px",
          minHeight: `${BADGE_HEIGHT_PX}px`,
          padding: "3px 9px 3px 3px",
          border: `1px solid ${color}`,
          borderRadius: "9999px",
          background: "var(--color-surface)",
          boxShadow: "var(--shadow-1)",
          color: "var(--color-text)",
          fontSize: "12px",
          fontWeight: 700,
          whiteSpace: "nowrap",
        }}
      >
        <ViewerAvatar viewer={viewer} />
        <span
          style={{
            minWidth: 0,
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {viewer.displayName}
        </span>
      </div>
    </div>
  );
}

function ViewerAvatar({ viewer }: { readonly viewer: Viewer }): JSX.Element {
  const commonStyle: CSSProperties = {
    width: "22px",
    height: "22px",
    borderRadius: "9999px",
    flex: "0 0 auto",
  };

  if (viewer.avatarImageUrl) {
    return (
      <img
        alt={`${viewer.displayName} のアイコン`}
        src={viewer.avatarImageUrl}
        style={{
          ...commonStyle,
          objectFit: "cover",
        }}
      />
    );
  }

  return (
    <span
      aria-hidden="true"
      style={{
        ...commonStyle,
        display: "inline-grid",
        placeItems: "center",
        background: normalizeViewerColor(viewer.color),
        color: "#ffffff",
        fontSize: "10px",
        lineHeight: 1,
      }}
    >
      {getFallbackLabel(viewer.displayName)}
    </span>
  );
}

function compareViewer(left: Viewer, right: Viewer): number {
  const byName = left.displayName.localeCompare(right.displayName, "ja");
  if (byName !== 0) return byName;
  return left.userId.localeCompare(right.userId, "ja");
}

function getFallbackLabel(displayName: string): string {
  const trimmed = displayName.trim().replace(/\s+/gu, "");
  if (trimmed.length === 0) return "?";
  return Array.from(trimmed).slice(0, 2).join("");
}

function normalizeViewerColor(color: string): string {
  return color.trim().length > 0 ? color : "#1e63d4";
}

function withAlpha(color: string, alpha: number): string {
  if (!/^#[0-9a-f]{6}$/iu.test(color)) return `rgba(30, 99, 212, ${alpha})`;
  const red = Number.parseInt(color.slice(1, 3), 16);
  const green = Number.parseInt(color.slice(3, 5), 16);
  const blue = Number.parseInt(color.slice(5, 7), 16);
  return `rgba(${red}, ${green}, ${blue}, ${alpha})`;
}
